import React from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  signUp,
  loginUser,
  getCurrentUser,
  setUserLanguage,
} from 'store/actions/userActions';
import { reactToast } from 'modules/common/helpers';
import { handleResponseError } from 'modules/common/helpers';

import {
  ConfidentialForm,
  SignUpHeader,
  SignUpLogoAndLanguage,
  SignUpPaymentForm,
  SignUpProfileForm,
} from 'modules/auth/components';

import { ERROR_CODES } from 'modules/common/constants';
import { LOCAL_STORAGE_VARS } from 'modules/common/constants/enums';

interface SignUpFormProps {
  customization: ClientCustomization;
  token?: string;
  client?: string;
  data?: any;
  fullName?: string;
  customFont?: string;
}

const STEP_1 = 'profile';
const STEP_2 = 'payment';
const STEP_3 = 'confidential';

export default function SignUpForm({
  customization,
  client,
  fullName,
}: SignUpFormProps) {
  const { t } = useTranslation(['common', 'auth']);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [profile, setProfile] = React.useState({});
  profile;
  const [paymentDetail, setPaymentDetail] = React.useState<any>({});
  paymentDetail;
  const [loading, setLoading] = React.useState(false);
  const [registerError, setRegisterError] = React.useState('');

  const [step, setStep] = React.useState(STEP_1);
  const saveProfileInfo = (profile) => {
    setProfile(profile);
    setStep(STEP_2);
  };

  const savePaymentDetail = (data) => {
    setPaymentDetail(data);
    setStep(STEP_3);
  };

  const handleSubmit = async (loginData) => {
    if (isEmpty(profile) || isEmpty(paymentDetail) || isEmpty(loginData)) {
      if (isEmpty(profile)) setStep(STEP_1);
      if (isEmpty(paymentDetail)) setStep(STEP_2);
      return;
    }

    const data = { ...profile, ...paymentDetail, ...loginData };
    const browserLang = localStorage.getItem(LOCAL_STORAGE_VARS.SITE_LANGUAGE);

    setLoading(true);
    // handle error check
    const response: any = await dispatch(signUp(data, { returnError: true }));
    const responseError = handleResponseError(response);

    if (responseError) {
      if (responseError === ERROR_CODES.USER_ALREADY_EXISTS) {
        setRegisterError(t(`errors:${responseError}`));
      } else {
        reactToast.showError(t(`errors:${responseError}`));
      }
    } else {
      await dispatch(loginUser(loginData));

      const userProfile: any = await dispatch(getCurrentUser());
      if (browserLang && userProfile?.language !== browserLang) {
        await dispatch(setUserLanguage(browserLang));
      }
      if (window.location.pathname !== 'verify-email') {
        navigate('/verify-email');
      }
      setLoading(false);
    }
  };

  const getCurrentForm = () => {
    switch (step) {
      case STEP_2:
        return <SignUpPaymentForm onSuccess={savePaymentDetail} />;
      case STEP_3:
        return (
          <ConfidentialForm
            onSubmit={handleSubmit}
            loading={loading}
            registerError={registerError}
            free={!paymentDetail?.iban}
          />
        );
      default:
        return <SignUpProfileForm onSuccess={saveProfileInfo} />;
    }
  };

  const getFormTitle = () => {
    switch (step) {
      case STEP_2:
        return t('auth:sign_up_payment_form_title');
      case STEP_3:
        return t('auth:sign_up_confidential_form_title');
      default:
        return t(`auth:default_signup_form_label`);
    }
  };

  return (
    <div className="t-min-h-screen t-flex t-items-center">
      <div className="t-pb-12 lg:t-pt-8 md:t-pb-12 md:t-px-16 md:t-max-w-sm t-bg-transparent">
        <div>
          <div className="lg:t-hidden">
            <SignUpLogoAndLanguage
              customization={customization}
              hideLanguageFlags={true}
            />
            <SignUpHeader
              customization={customization}
              client={client}
              fullName={fullName}
            />
          </div>

          <div
            className={`card t-shadow-lg t-bg-beta-50 t-p-6 t-mt-6 t-min-w-120`}
          >
            <h2 className={`typo-beta t-text-delta-900 t-mb-5`}>
              {getFormTitle()}
            </h2>

            {getCurrentForm()}
          </div>
        </div>
      </div>
    </div>
  );
}
